import { useCallback, useEffect, useRef, useState } from 'react';
import { Bell, CheckSquare, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn, truncateAction } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth';
import { useSocketStore } from '@/stores/socket';
import { useToast } from '@/components/ui/Toast';
import { formatRelative } from '@/components/TimelineSection';
import { eventsApi, isTaskOverdue, type EventItem } from '@/services/events.api';
import { useNotificationsCountStore } from '@/stores/notifications-count';
import { useRealtimeEvent, REALTIME_EVENTS, type TaskMutatedEvent } from '@/hooks/useRealtimeEvent';
import { EventFormModal } from './EventFormModal';
import { displayName } from '@shared/prospect';

/**
 * Cloche navbar - agenda personnel (tâches du jour + en retard + RDV du jour).
 * Distincte du système de notifications persistantes : la page /notifications
 * reste la trace historique des notifs (task_due, commission, etc.).
 *
 * Refresh : mount + `window.focus` + `visibilitychange` (onglet revient au
 * premier plan). Pas de polling. Les toasts WS d'alerte événementielle
 * (assignation direction, commission) continuent de s'afficher via le même
 * listener - mais n'alimentent plus le contenu de la cloche.
 */

const DOT_BY_PRIORITY = {
  neutral: 'bg-gray-400',
  accent: 'bg-accent',
  success: 'bg-success-600',
  warning: 'bg-warning-500',
  danger: 'bg-danger',
} as const;

export function NotificationBell() {
  const token = useAuthStore((s) => s.accessToken);
  const navigate = useNavigate();
  const toast = useToast();
  const onNotification = useSocketStore((s) => s.onNotification);
  const socket = useSocketStore((s) => s.socket);
  const incrementNotifCount = useNotificationsCountStore((s) => s.increment);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<EventItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<EventItem | null>(null);
  const ref = useRef<HTMLDivElement | null>(null);

  const loadAgenda = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await eventsApi.agenda(token);
      setItems(res.items);
      setTotal(res.total);
    } catch {
      /* silencieux - la cloche reste interactive même en cas d'échec ponctuel */
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Chargement initial + refetch au focus fenêtre / retour d'onglet.
  useEffect(() => {
    void loadAgenda();
    const onFocus = () => void loadAgenda();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void loadAgenda();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [loadAgenda]);

  // Listener WS : toast pour les notifs événementielles (assignation,
  // commission, etc.) + incrément du badge sidebar. On ne touche PAS au
  // contenu de la cloche - c'est l'agenda qui y est affiché.
  useEffect(() => {
    if (!socket) return;
    const unsubscribe = onNotification((n) => {
      toast({
        tone: n.priority === 'critical' || n.priority === 'high' ? 'warning' : 'info',
        title: n.title,
        message: n.message ?? n.title,
        duration: 5000,
      });
      // Badge Notifications sidebar +1 (la notif est non lue à la création).
      incrementNotifCount();
      // Si la notif concerne potentiellement une tâche (tâche assignée,
      // échéance) → refetch l'agenda pour que la cloche reflète la nouveauté.
      if (n.type === 'task_due' || n.type === 'task_assigned_by_direction') {
        void loadAgenda();
      }
    });
    return unsubscribe;
  }, [socket, onNotification, toast, loadAgenda, incrementNotifCount]);

  // Refresh temps réel sur mutations de tâches (complete, uncomplete, create,
  // update, delete) - évite d'avoir à attendre un focus fenêtre. L'event
  // `task.mutated` est émis par le backend aux recipients (assignee + créateur)
  // après chaque mutation. Payload minimal → on refetch l'agenda via API
  // authentifiée (RBAC rejoué côté serveur, pas de leak).
  useRealtimeEvent<TaskMutatedEvent>(
    REALTIME_EVENTS.TASK_MUTATED,
    useCallback(() => {
      void loadAgenda();
    }, [loadAgenda]),
  );

  // Fermeture par clic extérieur.
  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  function handleClickItem(ev: EventItem) {
    setOpen(false);
    setSelected(ev);
  }

  async function handleAgendaChanged() {
    // L'utilisateur a marqué une tâche faite / modifiée / supprimée dans la
    // modale → on rafraîchit la cloche pour refléter le nouvel état.
    await loadAgenda();
  }

  return (
    <>
      <div ref={ref} className="relative">
        <button
          type="button"
          aria-label="Agenda"
          onClick={() => setOpen((v) => !v)}
          className="relative flex h-9 w-9 items-center justify-center rounded-md text-text-muted transition-colors hover:bg-gray-100 hover:text-brand"
        >
          <Bell className="h-5 w-5" />
          {total > 0 && (
            <>
              <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-danger px-1 text-[10px] font-semibold text-white">
                {total > 99 ? '99+' : total}
              </span>
              <span className="absolute right-1 top-1 h-4 w-4 animate-ping rounded-full bg-danger/40" />
            </>
          )}
        </button>

        {open && (
          <div
            role="menu"
            className="absolute right-0 top-full z-30 mt-2 w-96 overflow-hidden rounded-lg border border-border-subtle bg-white shadow-popover animate-scale-in"
          >
            <div className="flex items-center justify-between border-b border-border-subtle px-4 py-2.5">
              <div>
                <div className="text-sm font-semibold text-brand">Mon agenda</div>
                <div className="text-[11px] text-text-muted">
                  {total === 0
                    ? 'Aucune tâche ni RDV à traiter'
                    : `${total} à traiter aujourd'hui ou en retard`}
                </div>
              </div>
            </div>
            <div className="max-h-96 overflow-y-auto">
              {loading && items.length === 0 && (
                <div className="py-8 text-center text-xs text-text-muted">Chargement…</div>
              )}
              {!loading && items.length === 0 && (
                <div className="py-8 text-center text-xs text-text-muted">
                  Rien à faire aujourd&apos;hui - bien joué.
                </div>
              )}
              {items.map((ev) => {
                // Refonte 2026-05-22 : isTaskOverdue couvre désormais aussi
                // les meetings (depuis la refonte 2026-05-21 meeting task-like).
                // Le helper retourne true pour tâche OU meeting passé non-done.
                const overdue = isTaskOverdue(ev);
                const isOverdueMeeting = overdue && ev.type === 'meeting';
                const isOverdueTask = overdue && ev.type === 'task';
                const when =
                  ev.type === 'task' && ev.taskDueDate
                    ? ev.taskDueDate
                    : (ev.scheduledAt ?? ev.createdAt);
                const Icon = ev.type === 'meeting' ? Users : CheckSquare;
                return (
                  <button
                    key={ev.id}
                    type="button"
                    onClick={() => handleClickItem(ev)}
                    className={cn(
                      'flex w-full items-start gap-3 border-b border-gray-100 px-4 py-3 text-left text-sm last:border-b-0 hover:bg-gray-50',
                      // Décision produit 2026-05-22 Q1 : tâches en retard
                      // restent rouge (danger), RDV en retard sont orange
                      // (warning) — distinction visuelle entre les 2 types.
                      isOverdueTask && 'bg-danger/5',
                      isOverdueMeeting && 'bg-warning/5',
                    )}
                  >
                    <span
                      className={cn(
                        'mt-1 h-2 w-2 shrink-0 rounded-full',
                        // Tâches non-faites = rouge (la cloche ne montre que
                        // les tâches non-done). Meetings à venir / du jour =
                        // accent. Meetings en retard = warning (orange).
                        ev.type === 'task'
                          ? DOT_BY_PRIORITY.danger
                          : isOverdueMeeting
                            ? 'bg-warning-500'
                            : 'bg-accent',
                      )}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-text-muted">
                        <Icon className="h-3 w-3" />
                        <span>{ev.type === 'meeting' ? 'RDV' : 'Tâche'}</span>
                        {isOverdueTask && <span className="text-danger">· En retard</span>}
                        {isOverdueMeeting && (
                          <span className="text-warning-600">· En retard</span>
                        )}
                      </div>
                      <div className="mt-0.5 truncate font-medium text-brand">
                        {truncateAction(ev.title) ||
                          (ev.type === 'meeting' ? 'RDV sans titre' : 'Tâche')}
                      </div>
                      <div className="mt-0.5 truncate text-xs text-text-primary">
                        {ev.prospect ? displayName(ev.prospect) : '-'}
                      </div>
                      <div className="mt-0.5 text-[11px] text-text-muted">
                        {formatRelative(when)}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="border-t border-border-subtle px-4 py-2 text-center">
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  navigate('/calendar?filter=tasks&scope=mine');
                }}
                className="text-xs text-accent hover:underline"
              >
                {total > items.length
                  ? `Voir toutes mes tâches (${total}) →`
                  : 'Voir toutes mes tâches →'}
              </button>
            </div>
          </div>
        )}
      </div>

      {selected && (
        <EventFormModal
          event={selected}
          onClose={() => setSelected(null)}
          onChange={() => {
            void handleAgendaChanged();
          }}
        />
      )}
    </>
  );
}
